"use client"

import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import Text from "@/app/(site)/_components/Atoms/Text"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { useProductDisplayInfo } from "@/app/(site)/_components/Organisms/Cards/OldProductCards/helper"
import { useIsMobile } from "@/app/(site)/_utils/_hooks/useIsMobile"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { t } from "@/translations"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import placeholder from "@assets/images/placeholder.png"
import { usePathname } from "next/navigation"

type ProductCardProps = {
    product: CheckoutAPIProduct
}

const RegularProductCard = ({ product }: ProductCardProps) => {
    return <SubRegularProductCard product={product} />
}

const SubRegularProductCard = ({ product }: { product: CheckoutAPIProduct }) => {
    const isMobile = useIsMobile()
    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""
    const {
        setCurrentProduct,
        uri,
        badges,
        colorVariantsAmount,
        sortedColor,
        name,
        extraColorsCount,
        price,
        priceBeforeDiscount,
        anyVariantisAvailable,
        imageUri,
        showAsOnSale,
        hoverImage
    } = useProductDisplayInfo(product)

    function handleHover() {
        if (
            !Array.isArray(product) &&
            product?.mediaObjects[1]?.sources?.standard[0]?.url &&
            isMobile === false
        ) {
            setCurrentProduct(prevState => ({
                ...prevState,
                imageUri: product?.mediaObjects[1]?.sources?.standard[0]?.url
            }))
        }
    }

    function handleMouseLeave() {
        if (
            !Array.isArray(product) &&
            product?.mediaObjects?.[0]?.sources?.standard[0]?.url &&
            isMobile === false
        ) {
            setCurrentProduct(prevState => ({
                ...prevState,
                imageUri: product?.mediaObjects?.[0]?.sources?.standard?.[0]?.url ?? ""
            }))
        }
    }
    function handleMouseEnterVariant(product: CheckoutAPIProduct | RelatedProduct) {
        let currentProduct = product as CheckoutAPIProduct
        if (Array.isArray(product)) {
            currentProduct = product[0]
        }
        return () => {
            const newImageUri = currentProduct?.media?.standard?.[0] ?? ""
            setCurrentProduct(prevState => ({
                ...prevState,
                imageUri: newImageUri,
                name: currentProduct.name,
                price: currentProduct.price,
                priceBeforeDiscount: currentProduct.priceBeforeDiscount,
                available: currentProduct.available,
                uri: currentProduct.uri,
                showAsOnSale: currentProduct.showAsOnSale,
                showAsNew: currentProduct.showAsNew
            }))
        }
    }

    return (
        <div className="group relative">
            <DynamicLink href={uri} prefetch={false}>
                <div className="cursor-pointer">
                    <div className="bg-[#f4f5f5]">
                        <div className="absolute left-0 top-0 flex">
                            {badges?.map((badge, index) => (
                                <Badge key={index} badge={badge} />
                            ))}
                        </div>
                        <div className="mb-16 aspect-square overflow-hidden">
                            <BaseImage
                                width={1000}
                                height={1000}
                                src={imageUri ?? placeholder}
                                alt={name}
                                className="object-contain"
                                onMouseEnter={handleHover}
                                onMouseLeave={handleMouseLeave}
                                sizes="(max-width: 968px) 33vw, 20vw"
                            />
                        </div>
                    </div>
                    <div className="w-full">
                        <Text size="small" weight="regular" className={`md:text-16`}>
                            {name}
                        </Text>
                        <Text
                            size="small"
                            weight="regular"
                            className="flex flex-wrap gap-x-8 break-words pb-8 text-main-dark-grey md:text-16"
                        >
                            <span>{price}</span>
                            {showAsOnSale && (
                                <span className="line-through">
                                    {priceBeforeDiscount}
                                </span>
                            )}
                        </Text>
                    </div>
                </div>
            </DynamicLink>
            <div className="flex h-38 w-full">
                <div className={`hidden md:group-hover:block`}>
                    <div className="flex w-full items-center">
                        {sortedColor.map((swatch, index) => {
                            return (
                                <DynamicLink
                                    href={`/product/${swatch.product.uri}`}
                                    className="cursor-pointer"
                                    onMouseEnter={handleMouseEnterVariant(
                                        swatch?.product
                                    )}
                                    onMouseLeave={handleMouseEnterVariant(
                                        product ?? product[0]
                                    )}
                                    key={index}
                                    prefetch={false}
                                >
                                    {swatch.color ? (
                                        <div className="m-4 h-24 w-24 rotate-[-50deg]">
                                            <div className="relative h-24 w-24">
                                                {swatch?.image ? (
                                                    <BaseImage
                                                        alt={swatch.product.name}
                                                        src={swatch.image.url}
                                                        width={100}
                                                        height={100}
                                                        className="absolute inset-0 z-20 rounded-full"
                                                        style={{
                                                            clipPath:
                                                                "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="absolute inset-0 rounded-full"
                                                        style={{
                                                            clipPath:
                                                                "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                            backgroundColor:
                                                                swatch?.product?.lens
                                                                    ?.hex ?? swatch.color
                                                        }}
                                                    ></div>
                                                )}
                                                <div
                                                    className={`absolute inset-0 rounded-full`}
                                                    style={{
                                                        clipPath:
                                                            "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                                        backgroundColor: swatch.color
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ) : (
                                        <></>
                                    )}
                                </DynamicLink>
                            )
                        })}
                        {extraColorsCount > 0 && (
                            <div>
                                <Text
                                    size="small"
                                    weight="regular"
                                    className="text-main-dark-grey md:text-16"
                                >
                                    +{extraColorsCount} {t(langCode, "more")}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
                {colorVariantsAmount > 0 && (
                    <div>
                        <Text
                            size="small"
                            weight="regular"
                            className={`text-main-dark-grey md:text-16 md:group-hover:hidden`}
                        >
                            {colorVariantsAmount} {t(langCode, "Colors")}
                        </Text>
                    </div>
                )}
            </div>
            {!anyVariantisAvailable && (
                <Text
                    size="small"
                    weight="regular"
                    className="text-main-black md:text-16"
                >
                    {t(langCode, "Out of stock")}
                </Text>
            )}
        </div>
    )
}

export default RegularProductCard
